import { useCallback, useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { TextField } from "@shared/components/TextField";
import { Form } from "@shared/components/Form";
import { ActionsContainer } from "../styles";
import { SubscriptionSubmitButton } from "@features/Subscription/components/styles";
import { useStartEmailSubscriptionFlow } from "@queries/subscription-flow/useStartEmailSubscriptionFlow";
import {
  StartEmailSubscriptionSchema,
  startEmailSubscriptionFormSchema,
} from "./start-email-subscription-form-helpers";

type Props = {
  planId: string;
  planName: string;
  onEmailSent?: () => void;
};

const RESEND_COOLDOWN_SECONDS = 60;

export const StartEmailSubscription = ({
  planId,
  planName,
  onEmailSent,
}: Props) => {
  const [sentToEmail, setSentToEmail] = useState<string | null>(null);
  const [sendCount, setSendCount] = useState(0);
  const [secondsUntilResend, setSecondsUntilResend] = useState(
    RESEND_COOLDOWN_SECONDS,
  );

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<StartEmailSubscriptionSchema>({
    defaultValues: { email: "" },
    resolver: yupResolver(startEmailSubscriptionFormSchema),
    reValidateMode: "onChange",
  });

  const mutation = useStartEmailSubscriptionFlow(planId, {
    onSuccess: (_, params) => {
      setSentToEmail(params.email);
      setSendCount((count) => count + 1);
      onEmailSent?.();
    },
  });

  useEffect(() => {
    if (!sentToEmail) return;
    setSecondsUntilResend(RESEND_COOLDOWN_SECONDS);
    const timer = setInterval(() => {
      setSecondsUntilResend((seconds) => Math.max(0, seconds - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [sentToEmail, sendCount]);

  const onSubmit = useCallback(
    (data: StartEmailSubscriptionSchema) => {
      mutation.mutate({ email: data.email });
    },
    [mutation],
  );

  if (sentToEmail) {
    return (
      <Box>
        <Typography variant="h5" mb={2}>
          Check your email
        </Typography>
        <Typography variant="body1" mb={3}>
          We sent a confirmation link to <strong>{sentToEmail}</strong>. Click
          the link to continue setting up your {planName} subscription.
        </Typography>
        <ActionsContainer>
          <Button
            variant="text"
            disabled={mutation.isPending || secondsUntilResend > 0}
            onClick={() => mutation.mutate({ email: sentToEmail })}
          >
            {secondsUntilResend > 0
              ? `Resend email (${secondsUntilResend}s)`
              : "Resend email"}
          </Button>
        </ActionsContainer>
      </Box>
    );
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)} aria-disabled={mutation.isPending}>
      <TextField
        label="Email address"
        name="email"
        type="email"
        placeholder="Enter your email"
        defaultValue=""
        inputProps={{ ...register("email") }}
        fieldError={errors.email}
      />
      <SubscriptionSubmitButton
        isLoading={mutation.isPending}
        loadingText="Sending..."
      >
        Continue with email
      </SubscriptionSubmitButton>
    </Form>
  );
};
